import { HOME_Y, HOP_FRAMES, TIMING, WIDTH, yRow } from './constants';
import { createFrog, frogBox } from './frog';
import { advanceHop, beginHop } from './movement';
import { overlaps, sweptCollision } from './collision';
import { ArcadeTimer } from './timer';
import { POINTS, progressPoints } from './scoring';
import { levelConfig, type LevelConfig } from './levels';
import { createTraffic, moveObject, vehicleBoxes } from './traffic';
import { createRiver, updatePlatformKind } from './river';
import { isSurfaced, mouthOpen, onMouth, supports } from './platforms';
import { createHomes, homeIndex } from './homes';
import { ladyPosition, updateHomes, type LadyBonus } from './bonuses';
import { createEnemies, updateEnemies } from './enemies';
import type { DeathCause, Direction, Enemy, Frog, GameEvent, GameState, Home, Platform, Vehicle } from './state';

export class GameEngine {
  state: GameState = 'TITLE';
  resumeState: GameState = 'PLAYING';
  frog: Frog = createFrog();
  vehicles: Vehicle[] = [];
  platforms: Platform[] = [];
  enemies: Enemy[] = [];
  homes: Home[] = createHomes();
  lady: LadyBonus | null = null;
  timer = new ArcadeTimer();
  config: LevelConfig = levelConfig(1);
  score=0; highScore=0; level=1; lives=3; startingLives=3; homeCount=0;
  frame=0; stateFrame=0; extraLifeAwarded=false; demo=false;
  deathCause: DeathCause | null = null;
  lastBonus=0;
  private queue: Direction[]=[];
  private events: GameEvent[]=[];
  private warning=false;
  constructor() { this.loadLevel(); }

  start(lives=3, demo=false): void {
    this.score=0;this.level=1;this.lives=lives;this.startingLives=lives;this.homeCount=0;
    this.extraLifeAwarded=false;this.demo=demo;this.frame=0;this.events=[];
    this.loadLevel();this.spawn();this.transition('READY');this.emit({type:'start'});
  }
  private loadLevel(): void {
    this.config=levelConfig(this.level);this.vehicles=createTraffic(this.config);
    this.platforms=createRiver(this.config);this.homes=createHomes();
    this.enemies=createEnemies(this.config,this.platforms);this.lady=null;this.frame=0;
  }
  private spawn(): void {
    this.frog=createFrog();this.timer.reset();this.queue=[];this.warning=false;this.deathCause=null;
  }
  transition(state: GameState): void { this.state=state;this.stateFrame=0;this.queue=[]; }
  input(direction: Direction): void { if (this.state==='PLAYING' && this.queue.length<4) this.queue.push(direction); }
  clearInput(): void {this.queue=[];}
  pause(): void {
    if (['READY','PLAYING','FROG_DYING','HOME_REACHED','LEVEL_COMPLETE'].includes(this.state)) {
      this.resumeState=this.state;this.state='PAUSED';this.queue=[];
    }
  }
  resume(): void { if (this.state==='PAUSED') this.state=this.resumeState; }
  drainEvents(): GameEvent[] { const events=this.events;this.events=[];return events; }
  private emit(e: GameEvent): void { if (!this.demo) this.events.push(e); }
  addScore(points: number): void {
    this.score+=points;
    if (!this.demo) this.highScore=Math.max(this.highScore,this.score);
    if (this.score>=POINTS.extraLife && !this.extraLifeAwarded) {
      this.extraLifeAwarded=true;this.lives++;this.emit({type:'extra-life'});
    }
  }
  kill(cause: DeathCause): void {
    if (this.state!=='PLAYING') return;
    this.deathCause=cause;this.frog.hop=null;this.frog.lady=false;
    this.transition('FROG_DYING');this.emit({type:'death',cause});
  }
  step(): void {
    if (['PAUSED','TITLE','GAME_OVER','SETTINGS','MAIN_MENU','HIGH_SCORE_ENTRY','BOOT','AUDIO_UNLOCK'].includes(this.state)) return;
    this.stateFrame++;
    if (this.state==='READY') {
      if (this.stateFrame>=TIMING.ready) {this.transition('PLAYING');this.emit({type:'ready'});} return;
    }
    if (this.state==='FROG_DYING') {
      if (this.stateFrame>=TIMING.death) {
        this.lives--;
        if (this.lives<=0) {this.transition('GAME_OVER');this.emit({type:'game-over'});}
        else {this.spawn();this.transition('READY');}
      } return;
    }
    if (this.state==='HOME_REACHED') {
      if (this.stateFrame>=TIMING.home) {
        if (this.homes.every(h=>h.occupied)) {this.addScore(POINTS.board);this.transition('LEVEL_COMPLETE');this.emit({type:'level'});}
        else {this.spawn();this.transition('READY');}
      } return;
    }
    if (this.state==='LEVEL_COMPLETE') {
      if (this.stateFrame>=TIMING.level) {this.level++;this.loadLevel();this.spawn();this.transition('READY');} return;
    }
    if (this.state!=='PLAYING' && this.state!=='ATTRACT_MODE') return;
    this.frame++;
    const f=this.frog;
    f.previousX=f.x;f.previousY=f.y;
    for (const v of this.vehicles) {
      const l=this.config.roads[v.lane-1]!;
      moveObject(v,this.frame>l.accelerateAt ? v.speed*2.2 : v.speed);
    }
    for (const p of this.platforms) {moveObject(p);updatePlatformKind(p,this.config);}
    updateEnemies(this.enemies,this.platforms,this.frame);
    updateHomes(this.homes,this.frame,this.level);
    this.updateLady();
    if (!f.hop && f.carrierId) {
      const p=this.platforms.find(p=>p.id===f.carrierId);
      if (p) f.x+=p.dx;
      else {const enemy=this.enemies.find(e=>e.id===f.carrierId&&e.active);if(enemy)f.x+=enemy.x-enemy.previousX;}
    }
    if (!f.hop && this.queue.length) {
      const direction=this.queue.shift()!;
      if (beginHop(f,direction)) this.emit({type:'hop'});
    }
    const landed=advanceHop(f);
    this.checkRoadAndEnemies();
    if (this.deathCause!==null) return;
    if (!f.hop) {
      if (landed) {
        const row=yRow(f.y);
        if (f.direction==='up') {this.addScore(progressPoints(row,f.furthestRow));f.furthestRow=Math.max(f.furthestRow,row);}
      }
      if (f.y<=HOME_Y) {this.reachHome();return;}
      this.checkRiver();
    }
    if (this.deathCause!==null) return;
    if (f.x+6<0 || f.x-6>WIDTH) {this.kill('edge');return;}
    const lady=ladyPosition(this.lady,this.platforms);
    if (!f.hop && lady && Math.abs(lady.x-f.x)<10 && Math.abs(lady.y-f.y)<5) {
      f.lady=true;this.lady!.active=false;this.emit({type:'lady'});
    }
    if (this.timer.tick()) this.kill('timeout');
    else if (this.timer.beats<=10 && !this.warning) {this.warning=true;this.emit({type:'warning'});}
  }
  private checkRoadAndEnemies(): void {
    const f=this.frog, prev=frogBox(f.previousX,f.previousY), now=frogBox(f.x,f.y);
    for (const v of this.vehicles) {
      const hit=vehicleBoxes(v,v.previousX).some(b=>sweptCollision(prev,now,b,v.dx)) ||
        (v.wrapped && vehicleBoxes(v).some(b=>overlaps(now,b)));
      if (hit) {this.kill(v.kind==='truck'?'truck':v.kind==='race'?'race':'car');return;}
    }
    for (const e of this.enemies) {
      if (!e.active) continue;
      if (e.kind==='otter' && !mouthOpen(this.frame)) continue;
      const box={x:e.previousX+(e.kind==='otter'?e.w-10:1),y:e.y-4,w:e.kind==='otter'?10:e.w-2,h:8};
      const dx=e.x-e.previousX;
      const hit=Math.abs(dx)<WIDTH/2?sweptCollision(prev,now,box,dx):overlaps(now,{...box,x:box.x+dx});
      if (hit) {this.kill(e.kind);return;}
    }
  }
  private checkRiver(): void {
    const f=this.frog;
    if (f.y>120 || f.y<56) {f.carrierId=null;return;}
    if (f.x+6<0 || f.x-6>WIDTH) {this.kill('edge');return;}
    const candidates=this.platforms.filter(p=>Math.abs(p.y-f.y)<2);
    const current=candidates.find(p=>p.id===f.carrierId);
    // A wrapped carrier does not wrap the frog. Keep its old trajectory until
    // the frog itself has fully left the edge, then remove the life.
    if (current?.wrapped && !f.hop) {this.kill('edge');return;}
    const platform=(current && supports(current,f.x,this.frame) ? current : candidates.find(p=>supports(p,f.x,this.frame)));
    if (!platform) {
      const otter=this.enemies.find(e=>e.kind==='otter'&&e.active&&Math.abs(e.y-f.y)<2&&f.x-3>=e.x&&f.x+3<=e.x+e.w-10);
      if(otter){f.carrierId=otter.id;return;}
      const diving=candidates.some(p=>f.x>=p.x && f.x<=p.x+p.w && !isSurfaced(p,this.frame));
      this.kill(diving?'turtle':'water');return;
    }
    if (onMouth(platform,f.x,this.frame)) {this.kill('crocodile');return;}
    f.carrierId=platform.id;
  }
  private reachHome(): void {
    const i=homeIndex(this.frog.x);
    if (i<0) {this.kill('wall');return;}
    const home=this.homes[i]!;
    if (home.occupied) {this.kill('occupied');return;}
    if (home.crocodile) {this.kill('crocodile');return;}
    const bonus=this.timer.beats*POINTS.beat + (home.fly?POINTS.fly:0) + (this.frog.lady?POINTS.lady:0);
    this.addScore(POINTS.home+bonus);this.lastBonus=bonus;this.homeCount++;
    if (home.fly) this.emit({type:'fly'});
    if (this.frog.lady) this.emit({type:'lady-home'});
    home.occupied=true;home.fly=false;home.crocodile=false;
    this.transition('HOME_REACHED');this.emit({type:'home',points:POINTS.home+bonus});
  }
  private updateLady(): void {
    if (this.frog.lady) return;
    if (this.frame%900===180) {
      const candidates=this.platforms.filter(p=>p.lane===2);
      const p=candidates[Math.floor(this.frame/900)%candidates.length];
      if (p) this.lady={platformId:p.id,offset:p.w/2,active:true};
    }
    if (this.frame%900===620 && this.lady) this.lady.active=false;
  }
  // Used by the deterministic demo planner; it runs the real collision engine.
  clone(): GameEngine {
    const e=new GameEngine();Object.assign(e,structuredClone({
      state:this.state,resumeState:this.resumeState,frog:this.frog,vehicles:this.vehicles,platforms:this.platforms,
      enemies:this.enemies,homes:this.homes,lady:this.lady,config:this.config,score:this.score,highScore:this.highScore,
      level:this.level,lives:this.lives,startingLives:this.startingLives,homeCount:this.homeCount,frame:this.frame,
      stateFrame:this.stateFrame,extraLifeAwarded:this.extraLifeAwarded,demo:true,deathCause:this.deathCause,lastBonus:this.lastBonus,
    }));e.timer.frames=this.timer.frames;e.warning=this.warning;return e;
  }
}

export { HOP_FRAMES };
